import {type FC, type ReactNode, useCallback, useEffect, useLayoutEffect, useRef} from 'react';
import {useEditor, EditorContent} from '@tiptap/react';
import type {Editor as IEditor} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import classNames from 'classnames';

import {Extension, extensions} from '@tiptap/core';

import {TimeEditorHashtagMentionExtension} from 'features/hashtags/extension';

import {urlRegex} from 'utils/paste';

import {getSelectedText} from 'utils/utils';

import {Slice, Fragment, DOMParser} from 'prosemirror-model';

import {useAppSelector} from 'stores/redux_store';
import {hasFormattingBar} from 'features/editor/selectors/has_formatting_bar';
import {sendToStatist} from 'packages/statist';

import type {Features, Props} from './types';
import {EditorContext, useEditorContext} from './context';
import {
    TimeWebkitEnhancedFormatMarksExtension,
    TimeWebkitCommandsExtension,
    TimeWebkitHardBreakExtension,
    TimeWebkitListItemExtension,
    TimeWebkitParagraphExtension,
    TimeWebkitTextExtension,
    TimeWebkitBoldExtension,
    TimeWebkitItalicExtension,
    TimeWebkitStrikeExtension,
} from './extensions/editor-custom-extensions';
import editorStyles from './editor.module.css';
import {FormattingBar} from './components/formatting-bar';
import {ActionsBar} from './components/actions-bar';
import {TimeEditorMentionExtension} from './extensions/mention';
import {TimeWebkitOrderedListExtension} from './extensions/ordered-list';

import {keymap} from './keydownHandler';
import {TimeWebappEditorKeymapExtension} from './extensions/keymap';

import {TimeWebkitMarkdownExtension, parseHtmlFromMarkdown} from './markdown-extension';
import {TimeEditorChannelMentionExtension} from './extensions/channel';
import {TimeEditorSlashCommandExtension} from './extensions/slash-command';
import {mapSlice, removeNonSelectableElements} from './utils';
import {TimeWebkitEditorPlaceholderPlugin} from './extensions/placeholder';
import {TimeWebkitCodeBlockExtension} from './extensions/code-block';
import {TimeWebkitEmojiExtension} from './extensions/emoji-search';
import {LINK_POPOVER_EVENT, TimeWebkitLinkExtension} from './extensions/link/link';
import {TimeWebkitCodeExtension} from './extensions/code/code';
import {LimitCounter} from './components/limit-counter';
import {MentionMark} from './extensions/mention/mention-mark';
import {toMarkdown} from './to_markdown';
import {TimeWebkitBulletListExtension} from './extensions/bullet-list/bullet-list-extension';
import {TimeWebkitTypographyExtension} from './extensions/typography/typography-extension';
import {TimeWebkitHorizontalRuleExtension} from './extensions/horizontal-rule/horizontal-rule-extension';
import {TimeWebkitHeadingExtension} from './extensions/heading/heading-extension';
import {TimeWebkitBlockquoteExtension} from './extensions/blockquote/blockquote-extension';
import {TimeWebkitTableExtension} from './extensions/table/table-extension';
import {TimeWebkitTableHeaderExtension} from './extensions/table/table-header-extension';
import {TimeWebkitTableRowExtension} from './extensions/table/table-row-extension';
import {TimeWebkitTableCellExtension} from './extensions/table/table-cell-extension';
import {LinkPreviewContextProvider, useLinkPreviewContext} from './components/link-popovers/context/link-preview';
import {LinkPreview} from './components/link-popovers/link-preview/link-preview';

const Content: FC<{
    bodySlot?: ReactNode;
    actionsSlot?: ReactNode;
    emojiPicker?: ReactNode;
    fileUploader?: ReactNode;
}> = (props) => {
    const {bodySlot, actionsSlot, ...actionsBarItems} = props;
    const {editor, maxCharacters, charactersCount} = useEditorContext();
    const isFormattingBarOpened = useAppSelector(hasFormattingBar);

    const containerRef = useRef<HTMLDivElement>(null);

    const handleFocusReturn = useCallback((event: MouseEvent) => {
        if (!containerRef?.current?.contains(event.target as Node)) {
            return;
        }

        editor?.chain().focus().run();
    }, [containerRef, editor]);

    return (
        <div
            ref={containerRef}
            className={classNames(editorStyles.editorWrapper, {[editorStyles.withFormattingBar]: isFormattingBarOpened})}
        >
            {isFormattingBarOpened && (
                <FormattingBar
                    outerRef={containerRef}
                    onClosePopover={handleFocusReturn}
                />
            )}
            {editor ? (
                <div className={editorStyles.limitCounterWrapper}>
                    <LinkPreview editor={editor}/>
                    <EditorContent
                        className={classNames(
                            editorStyles.editorContent,
                        )}
                        editor={editor}
                    />
                    <LimitCounter
                        max={maxCharacters}
                        current={charactersCount}
                        className={editorStyles.limitCounter}
                    />
                </div>
            ) : null}
            <div className={editorStyles.bodySlot}>{bodySlot}</div>
            <ActionsBar
                onClosePopover={handleFocusReturn}
                {...actionsBarItems}
            >
                {actionsSlot}
            </ActionsBar>
        </div>
    );
};

function getNodesFromHtmlString(html: string) {
    const innerHtml = html.trim();
    if (!innerHtml) {
        return null;
    }

    const template = document.createElement('template');
    template.innerHTML = innerHtml;
    const result = template.content.children;

    return result;
}

const coreExtensions = [
    extensions.ClipboardTextSerializer,
    extensions.Commands,
    extensions.Editable,
    extensions.FocusEvents,
    extensions.Tabindex,
];

const EMPTY_ARRAY: any[] = [];

declare global {
    interface Window {
        __EDITOR__: IEditor | null;
    }
}

const disableMentionsInExtensions = new Set([
    TimeWebkitCodeBlockExtension.name,
]);
const disableMentionsInMarks = new Set(['code']);

const EditorComponent = (props: Props) => {
    const {
        className,
        features = {},
        onEnterPress,
        onArrowUpPress,
        onReplyLastPost,
        onReactLastPost,
        channelId,
        teamId,
        ctrlSend,
        codeBlockOnCtrlEnter,
        charactersCount,
        maxCharacters,
        placeholder,
        predefinedMentions,
        predefinedChannels = EMPTY_ARRAY,
        predefinedHashtags = EMPTY_ARRAY,
        rootId,
        setEditor,
        fileAttachmentSlot,
        sendMenuSlot,
        emojiPicker,
        fileUploader,
        onContentChange,
        onCaretPositionChange,
        onHandleKeyDown,
        draftMessage,
        onEscPress,
        withinThread = false,
        withinDirect = false,
        useCustomGroupMentions,
        useLDAPGroupMentions,
        setServerError,
    } = props;

    const {codeBlock, emoji, mention} = features as Features;
    const {handleLinkPreviewEvent} = useLinkPreviewContext();

    const hasMentions = mention?.isActive;

    const editorExtensions = [
        ...coreExtensions,
        TimeWebappEditorKeymapExtension.configure({ctrlSend, codeBlockOnCtrlEnter}),
        StarterKit.configure({
            heading: false,
            paragraph: false,
            blockquote: false,
            orderedList: false,
            bulletList: false,
            hardBreak: false,
            listItem: false,
            horizontalRule: false,
            text: false,
            codeBlock: false,
            bold: false,
            italic: false,
            strike: false,
            code: false,
        }),
        TimeWebkitTextExtension,
        TimeWebkitBoldExtension,
        TimeWebkitItalicExtension,
        TimeWebkitStrikeExtension,
        TimeWebkitCodeExtension,
        TimeWebkitHeadingExtension,
        TimeWebkitParagraphExtension,
        TimeWebkitCodeBlockExtension.configure({ctrlSend: ctrlSend || codeBlockOnCtrlEnter}),
        TimeWebkitBlockquoteExtension,
        TimeWebkitOrderedListExtension,
        TimeWebkitBulletListExtension,
        TimeWebkitLinkExtension.configure({
            excludedMarks: [MentionMark],
            handleLinkPreviewEvent,
        }),
        TimeWebkitListItemExtension.configure({
            ctrlSend,
        }),
        TimeWebkitHorizontalRuleExtension,
        TimeWebkitEnhancedFormatMarksExtension,
        TimeWebkitTypographyExtension,
        Extension.create({
            addProseMirrorPlugins() {
                return [
                    keymap({
                        'Mod-b': () => {
                            sendToStatist('editor.format.tap', {formatType: 'bold', hotkey: true});
                            return false;
                        },
                        'Mod-i': () => {
                            sendToStatist('editor.format.tap', {formatType: 'italic', hotkey: true});
                            return false;
                        },
                        'Mod-Shift-x': () => {
                            this.editor.commands.toggleMark('strike');
                            sendToStatist('editor.format.tap', {formatType: 'strikethrough', hotkey: true});
                            return false;
                        },
                        'Mod-Shift-7': () => {
                            sendToStatist('editor.format.tap', {formatType: 'list_ordered', hotkey: true});
                            return false;
                        },
                        'Mod-Shift-8': () => {
                            sendToStatist('editor.format.tap', {formatType: 'list_bulleted', hotkey: true});
                            return false;
                        },
                        'Mod-Shift-9': () => {
                            sendToStatist('editor.format.tap', {formatType: 'quote', hotkey: true});
                            return false;
                        },
                        'Mod-Alt-Shift-c': () => {
                            sendToStatist('editor.format.tap', {formatType: 'code_block', hotkey: true});
                            return false;
                        },
                        'Mod-Shift-u': () => {
                            if (editor?.isActive('codeBlock') || editor?.isActive('mention')) {
                                return false;
                            }

                            this.editor.emit(LINK_POPOVER_EVENT);
                            sendToStatist('editor.format.tap', {formatType: 'link', hotkey: true});
                            return false;
                        },
                        'Shift-ArrowUp': () => {
                            return Boolean(onReplyLastPost?.());
                        },
                        'Shift-Mod-\\': () => {
                            return Boolean(onReactLastPost?.());
                        },
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        Enter: (state, __, ___, event) => {
                            const isIntoCodeBlock = state.selection.$from.parent.type.name === TimeWebkitCodeBlockExtension.name;
                            return !(ctrlSend || (codeBlockOnCtrlEnter && isIntoCodeBlock)) && Boolean(onEnterPress?.(event as unknown as React.KeyboardEvent<Element>));
                        },
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        ArrowUp: (_, __, ___, event) => {
                            return Boolean(onArrowUpPress?.(event as unknown as React.KeyboardEvent<Element>));
                        },
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        'Mod-Enter': (state, __, ___, event) => {
                            const isIntoCodeBlock = state.selection.$from.parent.type.name === TimeWebkitCodeBlockExtension.name;
                            return Boolean(ctrlSend || (codeBlockOnCtrlEnter && isIntoCodeBlock)) && Boolean(onEnterPress?.(event as unknown as React.KeyboardEvent<Element>));
                        },
                        Escape: () => {
                            onEscPress?.();
                            return true;
                        },
                    }),
                ];
            },
        }),
        TimeWebkitEditorPlaceholderPlugin.configure({
            placeholder,
        }),
        TimeWebkitMarkdownExtension,
        TimeWebkitHardBreakExtension,
        TimeWebkitCommandsExtension,
    ];

    if (hasMentions) {
        editorExtensions.push(
            MentionMark,
            TimeEditorMentionExtension.configure({
                channelId,
                teamId,
                predefinedMentions,
                withinThread,
                withinDirect,
                useGroupMentions: useCustomGroupMentions || useLDAPGroupMentions,
                disableInExtensions: disableMentionsInExtensions,
                disableInMarks: disableMentionsInMarks,
            }),
        );
    }

    editorExtensions.push(
        TimeEditorChannelMentionExtension.configure({
            teamId,
            predefinedChannels,
            withinThread,
        }),
    );

    editorExtensions.push(
        TimeEditorHashtagMentionExtension.configure({
            teamId,
            predefinedHashtags,
            withinThread,
        }),
    );

    editorExtensions.push(
        TimeEditorSlashCommandExtension.configure({
            teamId,
            channelId,
            rootId,
            withinThread,
            setServerError,
        }),
    );

    editorExtensions.push(
        TimeWebkitEmojiExtension.configure({withinThread}),
    );

    editorExtensions.push(
        TimeWebkitTableExtension,
        TimeWebkitTableHeaderExtension,
        TimeWebkitTableRowExtension,
        TimeWebkitTableCellExtension,
    );

    const editor = useEditor(
        {
            editorProps: {
                attributes: {
                    class: classNames(editorStyles.editor, className),
                    role: 'textbox',
                    id: 'post_textbox',
                    'data-testid': props.dataTestId,
                },
                handleKeyDown: (_, e) => {
                    if (!(e.altKey || e.shiftKey || e.ctrlKey || e.metaKey)) {
                        e.cancelBubble = true;
                    }
                    onHandleKeyDown(e);
                },
                transformPasted: (slice, view) => {
                    return mapSlice(slice, (node, index) => {
                        const mentionMark = node.marks.find((mark) => mark.type.name === 'mention');
                        if (mentionMark && node.isText && node.text !== `@${mentionMark.attrs.id}`) {
                            return view.state.schema.text(`@${mentionMark.attrs.id}`, node.marks);
                        }

                        if (node.type.name === 'tableRow' && index === 0) {
                            return node.copy(Fragment.fromArray(node.content.content.map((child) => {
                                return node.type.schema.nodes.tableHeader!.create(child.attrs, child.content, child.marks);
                            })));
                        }

                        if (node.type.name === 'codeBlock') {
                            return node.copy(Fragment.fromArray(node.content.content.map((child) => {
                                if (child.isText) {
                                    const text = child.text || '';
                                    const normalizedText = (text.trim() || text).replace(/\r\n?/g, '\n');

                                    return node.type.schema.text(normalizedText, child.marks);
                                }

                                return child;
                            }, [])));
                        }

                        return node;
                    });
                },
                transformPastedHTML: (html: string) => {
                    return removeNonSelectableElements(html);
                },
                clipboardTextParser: (text, $context, plainText, view) => {
                    const dom = document.createElement('div');

                    const html = parseHtmlFromMarkdown(text);
                    const nodes = getNodesFromHtmlString(html);

                    if (nodes) {
                        dom.append(...nodes);
                    }

                    return DOMParser.fromSchema(view.state.schema).parseSlice(dom, {
                        preserveWhitespace: true,
                        context: $context,
                    });
                },
                handlePaste: (view, clip) => {
                    const selection = view.state.tr.selection;
                    if (selection.from === selection.to) {
                        return false;
                    }

                    const selected = getSelectedText();
                    const urlRe = urlRegex();
                    if (selected && urlRe.test(selected)) {
                        const text = clip.clipboardData?.getData('text/plain');
                        if (!text) {
                            return false;
                        }

                        const marks = [];
                        const mark = view.state.schema.marks.link;
                        if (mark) {
                            const href = text.trim();
                            marks.push(mark.create({title: href, href}));
                        }
                        const node = view.state.schema.text(text, marks);
                        const tr = view.state.tr.replaceRange(
                            selection.from, selection.to, (new Slice(Fragment.from(node), 0, 0)));
                        view.dispatch(tr);
                        return true;
                    }

                    return false;
                },
            },
            onTransaction(props) {
                onCaretPositionChange?.(props.editor.state.selection.anchor || 0);
            },
            onUpdate(props) {
                onContentChange?.(toMarkdown(props.editor.state, props.editor.schema));
            },
            extensions: editorExtensions,
            enableCoreExtensions: false,
            parseOptions: {
                preserveWhitespace: 'full',
            },
        },
        [
            channelId,
            teamId,
            ctrlSend,
            codeBlockOnCtrlEnter,
            predefinedMentions,
            predefinedChannels,
            rootId,
            onReactLastPost,
            onReplyLastPost,
            onArrowUpPress,
            onEnterPress,
            onCaretPositionChange,
            onContentChange,
            onHandleKeyDown,
            useCustomGroupMentions,
            useLDAPGroupMentions,
            handleLinkPreviewEvent,
        ],
    );

    const shouldRenderDraft = useRef(Boolean(draftMessage && editor?.isEmpty));

    useEffect(() => {
        if (!shouldRenderDraft.current) {
            return;
        }

        editor?.chain().setMarkdown(draftMessage, false).focus().run();

        shouldRenderDraft.current = false;
    }, [draftMessage, editor]);

    useEffect(() => {
        setEditor?.(editor);
    }, [editor, setEditor]);

    // @TODO: убрать после вывода из фиче флага
    useLayoutEffect(() => {
        // eslint-disable-next-line no-underscore-dangle
        window.__EDITOR__ = editor;

        return () => {
            // eslint-disable-next-line no-underscore-dangle
            window.__EDITOR__ = null;
        };
    }, [editor]);

    return (
        <EditorContext.Provider
            value={{
                editor: editor || undefined,
                charactersCount,
                maxCharacters,
                features: {
                    codeBlock,
                    emoji,
                    mention,
                },
            }}
        >
            <Content
                bodySlot={fileAttachmentSlot}
                emojiPicker={emojiPicker}
                fileUploader={fileUploader}
                actionsSlot={sendMenuSlot}
            />
        </EditorContext.Provider>
    );
};

export const Editor = (props: Props) => {
    return (
        <LinkPreviewContextProvider>
            <EditorComponent {...props}/>
        </LinkPreviewContextProvider>
    );
};
